import {
	Alert,
	Box,
	Icons,
	Stack,
	Inline,
	Spinner,
	Heading,
	Text,
	Money,
	MoneySum,
	RadioInput,
	Button,
	Component,
} from "@sembark-travel/ui-base"
import { ButtonLink, Link } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useMemo, useState } from "react"
import useSWR from "swr"
import { LogTransactionForm, Transactions } from "../Accounting"
import { generatePath, useNavigate } from "../router-utils"
import { TFormattedMoney } from "@sembark-travel/money"
import { useAuthUser } from "../Auth"
import { EmailContent, TEmailInclusionOptions } from "../shared/EmailContent"
import { TripTransactions } from "../Accounting/Transactions"

export function TripAccountingsDetails({
	tripId,
	...props
}: { tripId: string } & Omit<
	React.ComponentProps<typeof Transactions>,
	"params" | "disableLogging" | "pageKey"
>) {
	const xhr = useXHR()
	const { data, error } = useSWR<{
		estimated_profit_percentage: number | null
		estimate_profit_percentage_in_cost_currency: number | null
		null_profit_reason: string | null
		// tax_percentage: number | null //Depricated after release of new pricing strategies (Per Component V2, Per Component Per Person V2)
		// amounts in package/given/selling currency
		total_given_price_in_given_currency: TFormattedMoney
		estimated_profit_in_given_currency: TFormattedMoney | null
		estimated_tax_in_given_currency: TFormattedMoney | null
		// amounts in costing currency
		total_given_price_in_cost_currency: TFormattedMoney
		estimated_tax_in_cost_currency: TFormattedMoney | null
		estimated_profit_in_cost_currency: TFormattedMoney | null
		total_booked_price_in_given_currency: TFormattedMoney | null
		// amounts in their own currencies
		total_booked_prices: Array<TFormattedMoney>
		pending_hotel_bookings_count: number
		pending_cab_bookings_count: number
		pending_activity_bookings_count: number
		pending_bookings_count: number
		hotel_bookings_booked_prices: Array<TFormattedMoney>
		cab_bookings_booked_prices: Array<TFormattedMoney>
		activity_bookings_booked_prices: Array<TFormattedMoney>
		flight_bookings_booked_prices: Array<TFormattedMoney>
		extra_bookings_booked_prices: Array<TFormattedMoney>
		visa_bookings_booked_prices: Array<TFormattedMoney>
		same_package_and_costing_currencies: boolean
		total_tax_applied_on_label:
			| "cost + markup"
			| "only markup"
			| "mixed"
			| "exc."
		total_tax_percentage_label: string
	}>(tripId ? `/trips/${tripId}/accounting` : null, (key: string) =>
		xhr.get(key).then((resp) => resp.data.data)
	)
	const transactionParams = useMemo(() => {
		return {
			attached_to_type: "trips",
			attached_to_id: tripId,
		}
	}, [tripId])
	const [showInPackageCurrency, setShowInPackageCurrency] = useState(false)
	if (!tripId) return null
	return (
		<Stack gap="8">
			<Stack gap="4">
				<Inline
					justifyContent="between"
					gap="8"
					alignItems="center"
					bgColor="default"
					padding="4"
					borderBottomWidth="1"
				>
					<Heading as="h3">Summary</Heading>
					<Box>
						<ButtonLink
							to={generatePath("/trips/:tripId/accounting/transactions/new", {
								tripId,
							})}
							level="secondary"
							status="primary"
						>
							<Icons.Plus /> Log Payment
						</ButtonLink>
					</Box>
				</Inline>
				{error ? (
					<Box paddingX="4">
						<Alert status="error">
							{error.message || "Something went wrong. Please try again later."}
						</Alert>
					</Box>
				) : null}
				<Box padding="4">
					{!data ? (
						<Spinner alignCenter padding="4" />
					) : (
						<Stack
							rounded="lg"
							boxShadow="lg"
							bgColor="default"
							padding="8"
							gap="4"
						>
							{!data.same_package_and_costing_currencies ? (
								<Inline gap="2">
									<label>
										<RadioInput
											name="show_in_package_currency"
											value="0"
											checked={!showInPackageCurrency}
											onChange={() =>
												setShowInPackageCurrency(!showInPackageCurrency)
											}
										/>{" "}
										{data.total_given_price_in_cost_currency.currency}
									</label>
									<label>
										<RadioInput
											name="show_in_package_currency"
											value="1"
											checked={showInPackageCurrency}
											onChange={() =>
												setShowInPackageCurrency(!showInPackageCurrency)
											}
										/>{" "}
										{data.total_given_price_in_given_currency.currency}
									</label>
								</Inline>
							) : null}
							<Inline gap={{ xs: "8", md: "12" }} flexWrap="wrap">
								<Stack gap="px">
									<Text
										fontWeight="semibold"
										whiteSpace="preserve"
										fontSize="md"
										color="muted"
									>
										Package Amount
									</Text>
									<Text fontSize="2xl" fontWeight="semibold">
										{showInPackageCurrency ? (
											<Money
												money={data.total_given_price_in_given_currency}
												showCurrency
											/>
										) : (
											<Money
												money={data.total_given_price_in_cost_currency}
												showCurrency
											/>
										)}
									</Text>
								</Stack>
								<Stack gap="2">
									<Stack gap="px">
										<Text
											fontWeight="semibold"
											whiteSpace="preserve"
											fontSize="md"
											color="muted"
										>
											Bookings
										</Text>
										{data.total_booked_price_in_given_currency &&
										showInPackageCurrency ? (
											<Text fontSize="2xl" fontWeight="semibold">
												<Money
													money={data.total_booked_price_in_given_currency}
													showCurrency
												/>
											</Text>
										) : (
											<Text fontSize="2xl" fontWeight="semibold">
												<MoneySum money={data.total_booked_prices} />
											</Text>
										)}
									</Stack>
									{data.total_booked_prices.length ? (
										<Inline gap="4">
											{data.hotel_bookings_booked_prices?.length ? (
												<Stack gap="px">
													<Text
														whiteSpace="preserve"
														color="muted"
														fontSize="sm"
													>
														Hotels
													</Text>
													<Text>
														<MoneySum
															money={data.hotel_bookings_booked_prices}
														/>
													</Text>
												</Stack>
											) : null}
											{data.cab_bookings_booked_prices?.length ? (
												<Stack gap="px">
													<Text
														whiteSpace="preserve"
														color="muted"
														fontSize="sm"
													>
														Cabs
													</Text>
													<Text>
														<MoneySum money={data.cab_bookings_booked_prices} />
													</Text>
												</Stack>
											) : null}
											{data.flight_bookings_booked_prices?.length ? (
												<Stack gap="px">
													<Text
														whiteSpace="preserve"
														color="muted"
														fontSize="sm"
													>
														Flights
													</Text>
													<Text>
														<MoneySum
															money={data.flight_bookings_booked_prices}
														/>
													</Text>
												</Stack>
											) : null}
											{data.activity_bookings_booked_prices?.length ? (
												<Stack gap="px">
													<Text
														whiteSpace="preserve"
														color="muted"
														fontSize="sm"
													>
														Activities
													</Text>
													<Text>
														<MoneySum
															money={data.activity_bookings_booked_prices}
														/>
													</Text>
												</Stack>
											) : null}
											{data.visa_bookings_booked_prices?.length ? (
												<Stack gap="px">
													<Text
														whiteSpace="preserve"
														color="muted"
														fontSize="sm"
													>
														Visa
													</Text>
													<Text>
														<MoneySum
															money={data.visa_bookings_booked_prices}
														/>
													</Text>
												</Stack>
											) : null}
											{data.extra_bookings_booked_prices?.length ? (
												<Stack gap="px">
													<Text
														whiteSpace="preserve"
														color="muted"
														fontSize="sm"
													>
														Others
													</Text>
													<Text>
														<MoneySum
															money={data.extra_bookings_booked_prices}
														/>
													</Text>
												</Stack>
											) : null}
										</Inline>
									) : null}
									{data.pending_bookings_count ? (
										<Link
											to={generatePath("/trips/:tripId/services-bookings", {
												tripId: String(tripId),
											})}
										>
											<Text color="warning" fontSize="sm">
												<Icons.Attention /> Some bookings are still pending.
											</Text>
										</Link>
									) : null}
								</Stack>
								<Stack gap="2">
									<Inline gap={{ xs: "8", md: "12" }} flexWrap="wrap">
										<Stack gap="px">
											<Stack gap="2">
												<Stack>
													<Inline gap="1">
														<Text
															fontWeight="semibold"
															whiteSpace="preserve"
															fontSize="md"
															color="muted"
														>
															Estm. Tax ({data.total_tax_percentage_label})
														</Text>

														{data.total_tax_applied_on_label ===
														"only markup" ? (
															<Text
																whiteSpace="preserve"
																color="muted"
																fontWeight="semibold"
															>
																(Only Markup)
															</Text>
														) : null}
													</Inline>
													{showInPackageCurrency &&
													data.estimated_tax_in_given_currency ? (
														<Text fontSize="2xl" fontWeight="semibold">
															<Money
																money={data.estimated_tax_in_given_currency}
																showCurrency
															/>
														</Text>
													) : data.estimated_tax_in_cost_currency ? (
														<Text fontSize="2xl" fontWeight="semibold">
															<Money
																money={data.estimated_tax_in_cost_currency}
																showCurrency
															/>
														</Text>
													) : (
														<Text fontSize="2xl" fontWeight="semibold">
															N/A
														</Text>
													)}
												</Stack>
											</Stack>
										</Stack>
										<Stack gap="px">
											<Text
												fontWeight="semibold"
												whiteSpace="preserve"
												fontSize="md"
												color="muted"
											>
												Estm. Profit
											</Text>
											<Text fontSize="2xl" fontWeight="semibold">
												{data.estimated_profit_in_given_currency &&
												showInPackageCurrency ? (
													<Money
														money={data.estimated_profit_in_given_currency}
														showCurrency
													/>
												) : data.estimated_profit_in_cost_currency &&
												  !showInPackageCurrency ? (
													<Money
														money={data.estimated_profit_in_cost_currency}
														showCurrency
													/>
												) : (
													"N/A"
												)}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text
												fontWeight="semibold"
												whiteSpace="preserve"
												fontSize="md"
												color="muted"
											>
												Estm. Profit %
											</Text>
											<Text fontSize="2xl" fontWeight="semibold">
												{showInPackageCurrency &&
												data.estimated_profit_in_given_currency &&
												data.estimated_profit_percentage !== null
													? data.estimated_profit_percentage
													: !showInPackageCurrency &&
														  data.estimated_profit_in_cost_currency &&
														  data.estimate_profit_percentage_in_cost_currency !==
																null
														? data.estimate_profit_percentage_in_cost_currency
														: "N/A"}
												<Box as="span" fontSize="sm" color="muted">
													%
												</Box>
											</Text>
										</Stack>
									</Inline>
									{data.null_profit_reason ? (
										<Box maxWidth={"md"}>
											<Text color="warning" fontSize="sm">
												<Icons.Attention /> {data.null_profit_reason}
											</Text>
										</Box>
									) : null}
								</Stack>
							</Inline>
						</Stack>
					)}
				</Box>
			</Stack>
			<Box paddingX="8">
				<TripReport tripId={tripId} />
			</Box>
			{data ? (
				<Box>
					<TripTransactions
						{...props}
						params={transactionParams}
						disableLogging
						pageKey={`/trips/${tripId}/accounting/transactions`}
					/>
				</Box>
			) : null}
		</Stack>
	)
}

function TripReport({
	tripId,
	disabledSharing,
}: {
	tripId: number | string
	disabledSharing?: boolean
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const canViewTripVoucher = Boolean(user?.tenant?.feature_flags?.trip_voucher)
	const fetchContent = useCallback(
		async (params?: object) => {
			return xhr
				.get<{
					data: {
						content: string
						options: TEmailInclusionOptions
						warning?: string
					}
				}>(`/trip-reports/${tripId}/as-email`, {
					params,
				})
				.then((resp) => resp.data.data)
		},
		[xhr, tripId]
	)
	if (!canViewTripVoucher) return null
	return (
		<Stack gap="4">
			<Heading as="h3">Profit Report</Heading>
			<Component initialState={true}>
				{({
					state: hideContentOnWarning,
					setState: setHideContentOnWarning,
				}) => (
					<EmailContent
						maxWidth="100%"
						inclusionOptionsCacheKey={`trip-report-${tripId}`}
						contentCacheKey={`/trip-reports/${tripId}/as-email`}
						fetchContent={fetchContent}
						copyTrackId="trip_reports"
						disabled={disabledSharing}
						downloadUrl={`/trip-reports/${tripId}/as-sheet`}
						downloadUrlLabel="Excel"
						hideContentOnWarning={hideContentOnWarning}
						actionOnWarningBanner={() => (
							<Button
								level="secondary"
								onClick={() => setHideContentOnWarning(!hideContentOnWarning)}
							>
								{hideContentOnWarning ? "Show" : "Hide"} Incomplete Report
							</Button>
						)}
					/>
				)}
			</Component>
		</Stack>
	)
}

export function LogTripTransaction({ tripId }: { tripId: string }) {
	const navigate = useNavigate()
	const xhr = useXHR()
	const back = useCallback(() => {
		navigate("/trips/:tripId/accounting/transactions", {
			params: { tripId },
			replace: true,
		})
	}, [navigate, tripId])
	return (
		<Stack>
			<Stack
				paddingX={{ xs: "4", xl: "6" }}
				paddingY="4"
				as="header"
				bgColor="default"
				borderBottomWidth="1"
			>
				<Heading as="h3">Log Payment for Trip</Heading>
			</Stack>
			<Box>
				<Stack padding="4" maxWidth="6xl" marginX="auto" gap="10">
					<Alert status="warning">
						<Box>
							To log instalment's payments (e.g. guest, hotel payments etc), use{" "}
							<Link to="../../payments" color="accent">
								Payments page
							</Link>{" "}
							for better and convenient accounting.
						</Box>
					</Alert>
					<LogTransactionForm
						hideTip
						onSubmit={async (data) => {
							await xhr.post(`/trips/${tripId}/accounting`, data)
							back()
						}}
						onCancel={back}
					/>
				</Stack>
			</Box>
		</Stack>
	)
}
